const STORAGE_KEY = "first_touch_attribution";

// Written on .inro.social by a script in the marketing site's head (Webflow
// custom code). See docs/SIGNUP_ATTRIBUTION.md for its format.
const WEBSITE_COOKIE = "inro_ft";

const UTM_KEYS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_content",
  "utm_term",
  "utm_id",
];

const CLICK_ID_KEYS = [
  "gclid",
  "gbraid",
  "wbraid",
  "fbclid",
  "msclkid",
  "ttclid",
];

// The website cookie's short keys, renamed to what the app stores.
const WEBSITE_KEYS = {
  ref: "$referrer",
  ref_domain: "$referring_domain",
  landing: "landing_path",
  ts: "first_touch_at",
};

const MAX_VALUE_LENGTH = 300;

const OWN_DOMAIN = /(^|\.)inro\.social$/i;

// Drops control characters and halves of emoji (the site cuts values at 200
// characters), which the server can't parse or store, and caps the length.
function cleanValue(value) {
  let clean = "";
  for (const char of value) {
    const code = char.codePointAt(0);
    if (code < 0x20 || code === 0x7f || (code >= 0xd800 && code <= 0xdfff)) {
      continue;
    }
    if (clean.length + char.length > MAX_VALUE_LENGTH) break;
    clean += char;
  }
  return clean;
}

function readAttributionFromCurrentPage() {
  const attribution = {};
  const params = new URLSearchParams(window.location.search);

  let referrerParams = new URLSearchParams();
  if (document.referrer) {
    attribution["$referrer"] = document.referrer;
    try {
      const referrerUrl = new URL(document.referrer);
      attribution["$referring_domain"] = referrerUrl.hostname;
      referrerParams = new URLSearchParams(referrerUrl.search);
    } catch {
      // malformed referrer, skip $referring_domain and its query params
    }
  }

  [...UTM_KEYS, ...CLICK_ID_KEYS].forEach((key) => {
    const value = params.get(key) || referrerParams.get(key);
    if (value) attribution[key] = value;
  });

  return Object.fromEntries(
    Object.entries(attribution)
      .map(([key, value]) => [key, cleanValue(value)])
      .filter(([, value]) => value),
  );
}

function readStoredAttribution() {
  try {
    const stored = JSON.parse(localStorage.getItem(STORAGE_KEY));
    const isObject =
      stored && typeof stored === "object" && !Array.isArray(stored);
    return isObject ? stored : {};
  } catch {
    return {};
  }
}

// The cookie is set by the visitor's browser, so only known keys holding
// strings are kept.
function readWebsiteAttribution() {
  try {
    const cookie = document.cookie
      .split(";")
      .map((entry) => entry.trim())
      .find((entry) => entry.startsWith(`${WEBSITE_COOKIE}=`));
    if (!cookie) return null;

    const data = JSON.parse(
      decodeURIComponent(cookie.slice(WEBSITE_COOKIE.length + 1)),
    );
    if (!data || typeof data !== "object") return null;

    const renames = [...UTM_KEYS, ...CLICK_ID_KEYS]
      .map((key) => [key, key])
      .concat(Object.entries(WEBSITE_KEYS));

    const attribution = {};
    renames.forEach(([from, to]) => {
      const value =
        typeof data[from] === "string" ? cleanValue(data[from]) : "";
      if (value) attribution[to] = value;
    });
    return attribution;
  } catch {
    return null;
  }
}

// A campaign tag or an ad click id, or a referral from a site that isn't ours.
function hasSource(attribution) {
  if ([...UTM_KEYS, ...CLICK_ID_KEYS].some((key) => attribution[key])) {
    return true;
  }
  const domain = attribution["$referring_domain"];
  return Boolean(domain) && !OWN_DOMAIN.test(domain);
}

// Captures the page's campaign tags, ad click ids and referrer, and persists
// them so they survive navigation (including the full-page redirect through
// Google OAuth) until sign-up. A first touch with a source is never replaced;
// one without (only our own site as referrer) gives way to a later visit that
// has one.
export function captureFirstTouchAttribution() {
  try {
    const stored = readStoredAttribution();
    if (hasSource(stored)) return;
    const attribution = readAttributionFromCurrentPage();
    if (!Object.keys(attribution).length) return;
    if (Object.keys(stored).length && !hasSource(attribution)) return;
    attribution.first_touch_at = new Date().toISOString();
    localStorage.setItem(STORAGE_KEY, JSON.stringify(attribution));
  } catch {
    // localStorage unavailable (private browsing, disabled, etc.)
  }
}

// The marketing site saw the page a visitor first landed on, while the app
// only sees the click from the site, so its record wins when it has a source.
export function getFirstTouchAttribution() {
  const website = readWebsiteAttribution();
  if (website && hasSource(website)) return website;
  const stored = readStoredAttribution();
  if (hasSource(stored)) return stored;
  return { ...stored, ...website };
}

// Stores the OAuth client as `signup_via`; its campaign tags only fill in for a
// visitor with no source of their own, so a real first touch is never replaced.
export function signupAttribution(
  firstTouch,
  { utmOverrides, signupVia } = {},
) {
  const attribution = hasSource(firstTouch)
    ? { ...firstTouch }
    : { ...firstTouch, ...utmOverrides };
  if (signupVia) attribution.signup_via = signupVia;
  return attribution;
}
